import React from 'react';
import { Badge, Box, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { DocumentDataStatusFilters } from '../../../types/packager-types';
import { formatExclusionReason } from '../../../types/packager-enum-descriptions';

type DocumentDataStatusProps = {
  status: DocumentDataStatusFilters | null | undefined;
  exclusionReason?: string | null;
  escalationDetail?: string | null;
  onClick?: () => void;
  // When true, show exclusion/escalation detail only as a tooltip on the badge
  // (no second line). Used in the review header where vertical space is tight.
  trailingValueAsTooltip?: boolean;
};

const DocumentDataStatus: React.FC<DocumentDataStatusProps> = ({
  status,
  exclusionReason,
  escalationDetail,
  onClick,
  trailingValueAsTooltip = false,
}) => {
  if (!status) {
    return <Typography variant="caption">N/A</Typography>;
  }

  const getStatusStyles = (status: DocumentDataStatusFilters) => {
    const baseStyles = {
      borderRadius: '4px',
      padding: '4px 6px',
      cursor: onClick ? 'pointer' : 'default',
    };

    switch (status) {
      case DocumentDataStatusFilters.PENDING_CREATION:
        return {
          ...baseStyles,
          backgroundColor: '#44556626', // 15% opacity of #445566
          color: '#445566',
        };
      case DocumentDataStatusFilters.MISCURATED:
        return {
          ...baseStyles,
          backgroundColor: '#FFB74D26',
          color: '#E65100',
        };
      case DocumentDataStatusFilters.ESCALATED:
        return {
          ...baseStyles,
          backgroundColor: '#E5393526',
          color: '#B71C1C',
        };
      case DocumentDataStatusFilters.TRANSMISSION_SUCCESSFUL:
        return {
          ...baseStyles,
          backgroundColor: '#62C65526', // 15% opacity of #62C655
          color: '#62C655',
        };
      case DocumentDataStatusFilters.TRANSMISSION_FAILED:
        return {
          ...baseStyles,
          backgroundColor: '#EF726726', // 15% opacity of #EF7267
          color: '#EF7267',
        };
      case DocumentDataStatusFilters.VERIFIED:
        return {
          ...baseStyles,
          backgroundColor: '#32668C26', // 15% opacity of #32668C
          color: '#32668C',
        };
      case DocumentDataStatusFilters.REVIEWED:
        return {
          ...baseStyles,
          backgroundColor: '#65BBA926', // 15% opacity of #65BBA9
          color: '#65BBA9',
        };
      case DocumentDataStatusFilters.PENDING_REVIEW:
        return {
          ...baseStyles,
          backgroundColor: '#9CA4B926', // 15% opacity of #9CA4B9
          color: '#5F677B',
        };
      case DocumentDataStatusFilters.EXCLUDED:
        return {
          ...baseStyles,
          backgroundColor: '#fb8c0026', // 15% opacity of #fb8c00
          color: '#fb8c00',
        };
      case DocumentDataStatusFilters.PENDING_SPLIT:
        return {
          ...baseStyles,
          backgroundColor: '#7E57C226', // 15% opacity of #7E57C2
          color: '#5E35B1',
        };
      default:
        return baseStyles;
    }
  };

  const label = status.replace(/_/g, ' ');
  const styles = getStatusStyles(status) as { color?: string };

  let trailingValue: string | null = null;
  if (status === DocumentDataStatusFilters.EXCLUDED && exclusionReason) {
    trailingValue = formatExclusionReason(exclusionReason);
  } else if (status === DocumentDataStatusFilters.ESCALATED && escalationDetail) {
    trailingValue = escalationDetail;
  }

  const badge = (
    <Box
      sx={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '2px',
        maxWidth: '100%',
        cursor: onClick ? 'pointer' : 'default',
      }}
      onClick={onClick}
    >
      <Badge
        sx={{ ...getStatusStyles(status), display: 'inline-flex' }}
        data-cy="badge-document-data-status"
      >
        <Typography
          sx={{
            fontSize: '10px',
            fontWeight: 'bold',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {label}
        </Typography>
      </Badge>
      {trailingValue && !trailingValueAsTooltip && (
        <Typography
          sx={{
            fontSize: '11px',
            fontWeight: 600,
            color: styles.color || 'text.primary',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '180px',
          }}
          data-cy="badge-document-data-status-value"
        >
          {trailingValue}
        </Typography>
      )}
    </Box>
  );

  if (trailingValueAsTooltip && trailingValue) {
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
        {badge}
        <Tooltip title={trailingValue} placement="top" arrow>
          <InfoOutlinedIcon
            data-cy="icon-document-data-status-info"
            sx={{
              fontSize: '14px',
              color: styles.color || 'text.secondary',
              cursor: 'help',
            }}
          />
        </Tooltip>
      </Box>
    );
  }

  return trailingValue && status === DocumentDataStatusFilters.ESCALATED ? (
    <Tooltip title={trailingValue} placement="top" arrow>
      {badge}
    </Tooltip>
  ) : (
    badge
  );
};

export default DocumentDataStatus;
